/**
 * BillingScreen — gestion de l'abonnement courant.
 * Adapté de cvh-app/src/app/settings/billing/page.tsx.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { CreditCard, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { UniqueButton } from '@components/UniqueButton';
import { UniqueCard } from '@components/UniqueCard';
import { UniqueBadge } from '@components/UniqueBadge';
import { useNavStore } from '@store/navStore';
import { useUserStore, getToken } from '@store/userStore';
import { PLAN_LABELS, PLAN_PRICES, PLAN_LIMITS, type PlanKey } from '@/lib/plans';
import { AuthService } from '@services/AuthService';

interface SubData {
  plan: PlanKey;
  plan_label: string;
  subscription: {
    status: string;
    months_paid: number;
    period_start: string | null;
    period_end: string | null;
    yenga_reference: string | null;
  } | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export default function BillingScreen(): JSX.Element {
  const navigate = useNavStore((s) => s.navigate);
  const profile = useUserStore((s) => s.profile);
  const [data, setData] = useState<SubData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    AuthService.getSubscriptionStatus(token)
      .then((d) => setData(d as SubData))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const plan = (data?.plan ?? profile?.plan ?? 'free') as PlanKey;
  const sub = data?.subscription ?? null;
  const isActive = sub?.status === 'active';
  const limits = PLAN_LIMITS[plan];

  const FEATURES: { label: string; value: string; ok: boolean }[] = [
    {
      label: 'Tâches',
      value: limits.maxTasks === -1 ? 'Illimitées' : `${limits.maxTasks} max`,
      ok:    limits.maxTasks === -1,
    },
    {
      label: 'Briefing IA / jour',
      value: limits.aiBriefingPerDay === -1 ? 'Illimité' : `${limits.aiBriefingPerDay}/jour`,
      ok:    limits.aiBriefingPerDay > 1 || limits.aiBriefingPerDay === -1,
    },
    {
      label: 'Brouillons IA / mois',
      value: limits.aiDraftsPerMonth === -1 ? 'Illimités' : limits.aiDraftsPerMonth === 0 ? 'Non disponible' : `${limits.aiDraftsPerMonth}/mois`,
      ok:    limits.aiDraftsPerMonth !== 0,
    },
    { label: 'Mode Focus',       value: limits.focusMode       ? 'Inclus' : 'Non disponible', ok: limits.focusMode       },
    { label: 'CommHub (actions)',value: limits.commHubActions   ? 'Inclus' : 'Non disponible', ok: limits.commHubActions   },
    { label: 'Analytics 30 j',  value: limits.analytics30Days  ? 'Inclus' : 'Non disponible', ok: limits.analytics30Days  },
    { label: 'Export PDF',       value: limits.pdfExport        ? 'Inclus' : 'Non disponible', ok: limits.pdfExport        },
    { label: 'Escalades',        value: limits.escalations      ? 'Inclus' : 'Non disponible', ok: limits.escalations      },
    { label: 'SmartSave',        value: limits.smartSave        ? 'Inclus' : 'Non disponible', ok: limits.smartSave        },
  ];

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="!border-none !pb-0">Mon abonnement</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-medium)' }}>
          Gérez votre plan UNIQUE
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 py-12">
          <span className="unique-loader" />
          <p className="text-sm" style={{ color: 'var(--color-text-medium)' }}>
            Chargement…
          </p>
        </div>
      ) : (
        <>
          {/* Plan courant */}
          <UniqueCard padding="lg" variant="glow" className="mb-5">
            <div className="flex items-start justify-between">
              <div>
                <p
                  className="text-xs font-bold uppercase tracking-widest mb-1"
                  style={{ color: 'var(--color-primary-dark)' }}
                >
                  Plan actuel
                </p>
                <h2 className="!text-2xl !border-none !pb-0">{PLAN_LABELS[plan]}</h2>

                {isActive && plan !== 'free' && sub?.period_end && (
                  <p className="text-sm mt-1" style={{ color: 'var(--color-text-medium)' }}>
                    Actif jusqu'au{' '}
                    <span style={{ color: 'var(--color-text-dark)', fontWeight: 600 }}>
                      {formatDate(sub.period_end)}
                    </span>
                  </p>
                )}
                {plan === 'free' && (
                  <p className="text-sm mt-1" style={{ color: 'var(--color-text-medium)' }}>
                    Passez à Essentiel pour débloquer toutes les fonctionnalités
                  </p>
                )}
              </div>

              <div className="text-right flex flex-col items-end gap-2">
                {plan !== 'free' && (
                  <p className="text-sm" style={{ color: 'var(--color-text-medium)' }}>
                    {PLAN_PRICES[plan].toLocaleString('fr-FR')} FCFA/mois
                  </p>
                )}
                <UniqueBadge tone={isActive && plan !== 'free' ? 'important' : plan === 'free' ? 'gold' : 'urgent'}>
                  {isActive && plan !== 'free' ? 'Actif' : plan === 'free' ? 'Gratuit' : 'Expiré'}
                </UniqueBadge>
              </div>
            </div>

            {/* Détails abonnement */}
            {sub && plan !== 'free' && (
              <div
                className="mt-4 pt-4 grid grid-cols-2 gap-4 text-sm border-t"
                style={{ borderColor: 'var(--color-border)' }}
              >
                {[
                  { label: 'Début', value: formatDate(sub.period_start) },
                  { label: 'Expiration', value: formatDate(sub.period_end) },
                  { label: 'Mois payés', value: `${sub.months_paid} mois` },
                  { label: 'Référence', value: sub.yenga_reference ?? '—' },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <p className="text-xs" style={{ color: 'var(--color-text-medium)' }}>{label}</p>
                    <p
                      className="font-semibold text-xs font-mono truncate"
                      style={{ color: 'var(--color-text-dark)' }}
                    >
                      {value}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </UniqueCard>

          {/* Fonctionnalités incluses */}
          <UniqueCard padding="lg" className="mb-6">
            <h3 className="text-sm font-bold mb-4 flex items-center gap-2">
              <CreditCard size={16} style={{ color: 'var(--color-primary-dark)' }} />
              Fonctionnalités incluses
            </h3>
            <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
              {FEATURES.map((f) => (
                <div key={f.label} className="flex items-center justify-between py-2">
                  <span className="text-sm" style={{ color: 'var(--color-text-dark)' }}>
                    {f.label}
                  </span>
                  <span
                    className="text-sm font-medium flex items-center gap-1"
                    style={{ color: f.ok ? 'var(--color-important)' : 'var(--color-text-medium)' }}
                  >
                    {f.ok
                      ? <CheckCircle size={13} />
                      : <XCircle size={13} />}
                    {f.value}
                  </span>
                </div>
              ))}
            </div>
          </UniqueCard>

          {/* Actions */}
          <div className="flex gap-3 flex-wrap">
            <UniqueButton rightIcon={<ArrowRight size={16} />} onClick={() => navigate('pricing')}>
              {plan === 'free' || sub?.status === 'expired'
                ? 'Passer à Essentiel'
                : 'Renouveler / Changer de plan'}
            </UniqueButton>
            <UniqueButton variant="secondary" onClick={() => navigate('dashboard')}>
              Retour au tableau de bord
            </UniqueButton>
          </div>
        </>
      )}
    </div>
  );
}
